import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { PortalProvider } from './contexts/PortalContext';

// Eager load critical pages (login is the entry point)
import { LoginPage } from './pages';

// Lazy load non-critical pages for better initial load performance
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const AuthorizePage = lazy(() => import('./pages/AuthorizePage'));
const CallbackPage = lazy(() => import('./pages/CallbackPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const LogoutPage = lazy(() => import('./pages/LogoutPage'));
const AccessDeniedPage = lazy(() => import('./pages/AccessDeniedPage'));
const ProfileSelectionPage = lazy(() => import('./pages/ProfileSelectionPage'));
const CompanySelectorPage = lazy(() => import('./pages/CompanySelectorPage'));
const GetStartedPage = lazy(() => import('./pages/GetStartedPage'));
const JoinOrganizationPage = lazy(() => import('./pages/JoinOrganizationPage'));
const CreateOrganizationPage = lazy(() => import('./pages/CreateOrganizationPage'));

/**
 * Main App component with routing configuration
 * 
 * Performance optimizations:
 * - Lazy loading for non-critical routes
 * - Code splitting by route
 * - Suspense boundaries for loading states
 * 
 * Routes:
 * - Public routes: login, register, forgot-password, reset-password, verify-email, oauth/authorize, oauth/callback
 * - Protected routes: account, logout
 */
function App() {
  return (
    <BrowserRouter>
      <PortalProvider>
        <AuthProvider>
        {/* Skip link for keyboard navigation */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Default route - redirect to login */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            
            {/* OAuth routes - lazy loaded as they're less frequently accessed */}
            <Route path="/oauth/authorize" element={<AuthorizePage />} />
            <Route path="/oauth/callback" element={<CallbackPage />} />
            
            {/* Access denied route - shown when user lacks platform access (REQ-1.2.3) */}
            <Route path="/access-denied" element={<AccessDeniedPage />} />
            
            {/* Profile selection route - shown when user has multiple profiles */}
            <Route
              path="/select-profile"
              element={
                <ProtectedRoute>
                  <ProfileSelectionPage />
                </ProtectedRoute>
              }
            />
            
            {/* Company selection route - shown when user has multiple tenants */}
            <Route
              path="/select-company"
              element={
                <ProtectedRoute skipTenantCheck>
                  <CompanySelectorPage />
                </ProtectedRoute>
              }
            />
            
            {/* Get Started route - shown when user has zero memberships (Req 4.1, 4.4) */}
            <Route
              path="/get-started"
              element={
                <ProtectedRoute skipTenantCheck requireZeroMemberships>
                  <GetStartedPage />
                </ProtectedRoute>
              }
            />
            
            {/* Join Organization route - enter invite code to join an org (Req 8.1) */}
            <Route
              path="/join"
              element={
                <ProtectedRoute skipTenantCheck>
                  <JoinOrganizationPage />
                </ProtectedRoute>
              }
            />
            
            {/* Create Organization route - create a new org from auth-portal (Req 8.4) */}
            <Route
              path="/create-org"
              element={
                <ProtectedRoute skipTenantCheck>
                  <CreateOrganizationPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected routes - lazy loaded */}
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/logout"
              element={
                <ProtectedRoute>
                  <LogoutPage />
                </ProtectedRoute>
              }
            />
            
            {/* 404 - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
      </PortalProvider>
    </BrowserRouter>
  );
}

export default App;
